import { useState } from 'react'
import { ChevronDown, Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface SingleSelectOption {
  id: string
  label: string
  /** Optional trailing count, rendered muted (e.g. how many fields a category holds). */
  count?: number
  /** Optional second line, rendered muted (e.g. why a field is suggested). */
  hint?: string
}

interface Props {
  options: SingleSelectOption[]
  /** Empty string means "nothing selected" — the placeholder / reset row is shown. */
  value: string
  onChange: (id: string) => void
  /** Shown on the trigger when nothing is selected. */
  placeholder?: string
  /** Label of the reset row pinned to the top of the list (selecting it clears the value). */
  resetLabel?: string
  searchPlaceholder?: string
  /** Threshold above which a search box appears. */
  searchThreshold?: number
  className?: string
  /** aria-label for the trigger — the filter has no visible <label>. */
  ariaLabel?: string
  /** 'sm' = compact 8-height trigger with xs text (for dense panel rows). */
  size?: 'default' | 'sm'
  /**
   * A short labeled group pinned above the options (e.g. suggested fields).
   * The search filters it with the rest; when nothing in it matches, or it is
   * empty, it is not shown at all.
   */
  pinned?: { label: string; options: SingleSelectOption[] }
}

/**
 * Compact searchable single-select rendered as an in-flow disclosure (not a
 * floating overlay) so it never clips inside a scroll container. Mirrors
 * `MultiSelect` — collapsed by default, the trigger summarizes the selection,
 * expanding reveals a searchable, bounded list that closes on select. A quick
 * ✕ clears an active selection without opening the list.
 */
export function SingleSelect({
  options,
  value,
  onChange,
  placeholder = 'All',
  resetLabel,
  searchPlaceholder = 'Search…',
  searchThreshold = 6,
  className,
  ariaLabel,
  size = 'default',
  pinned,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const selected = options.find(o => o.id === value) ?? pinned?.options.find(o => o.id === value) ?? null

  const q = query.trim().toLowerCase()
  const visible = q ? options.filter(o => o.label.toLowerCase().includes(q)) : options
  const visiblePinned = (pinned?.options ?? []).filter(o => !q || o.label.toLowerCase().includes(q))

  const select = (id: string) => {
    onChange(id)
    setOpen(false)
    setQuery('')
  }

  const clear = () => {
    onChange('')
    setQuery('')
  }

  return (
    <div className={cn('relative rounded-md border border-input bg-background', className)}>
      <button
        type='button'
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label={ariaLabel}
        className={cn(
          'flex w-full items-center rounded-md pl-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          size === 'sm' ? 'h-8 text-xs' : 'h-10 text-sm',
          selected ? 'pr-14' : 'pr-9',
          !selected && 'text-muted-foreground',
        )}
      >
        <span className='truncate'>{selected ? selected.label : placeholder}</span>
      </button>
      <ChevronDown
        className={cn(
          'pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-60 transition-transform motion-reduce:transition-none',
          open && 'rotate-180',
        )}
      />
      {selected && (
        <button
          type='button'
          onClick={clear}
          aria-label='Clear filter'
          className='absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring'
        >
          <X className='h-3.5 w-3.5' />
        </button>
      )}

      {open && (
        <div className='border-t'>
          {options.length > searchThreshold && (
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className='w-full border-b bg-transparent px-3 py-1.5 text-sm outline-none placeholder:text-muted-foreground'
            />
          )}
          <div className='max-h-56 overflow-y-auto p-1'>
            {resetLabel && !q && (
              <button
                type='button'
                onClick={() => select('')}
                className='flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent'
              >
                <Check
                  className={cn('h-3.5 w-3.5 shrink-0', value === '' ? 'opacity-100' : 'opacity-0')}
                />
                <span className='truncate'>{resetLabel}</span>
              </button>
            )}
            {visiblePinned.length > 0 && (
              <div role='group' aria-label={pinned!.label} className='mb-1 border-b pb-1'>
                <p className='px-2 pb-0.5 pt-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground'>
                  {pinned!.label}
                </p>
                {visiblePinned.map(o => (
                  <button
                    key={o.id}
                    type='button'
                    onClick={() => select(o.id)}
                    title={o.hint ? `${o.label} — ${o.hint}` : o.label}
                    className='flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent'
                  >
                    <Check className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', value === o.id ? 'opacity-100' : 'opacity-0')} />
                    <span className='min-w-0 flex-1'>
                      <span className='block truncate'>{o.label || 'Untitled'}</span>
                      {o.hint && <span className='block truncate text-[11px] text-muted-foreground'>{o.hint}</span>}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {visible.length === 0 ? (
              <p className='px-2 py-3 text-center text-xs text-muted-foreground'>No matches.</p>
            ) : (
              visible.map(o => (
                <button
                  key={o.id}
                  type='button'
                  onClick={() => select(o.id)}
                  className='flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent'
                >
                  <Check
                    className={cn(
                      'h-3.5 w-3.5 shrink-0',
                      value === o.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className='min-w-0 flex-1 truncate'>{o.label || 'Untitled'}</span>
                  {o.count != null && (
                    <span className='shrink-0 text-xs text-muted-foreground'>{o.count}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
